import React, { createRef, RefObject } from "react";
import { FormattedMessage } from "react-intl";
import { CSSTransition } from "react-transition-group";

import { EmbedBlockModel } from "@courses-models/course";
import { asyncTimeout } from "@use/async-timeout";
import { hrtime } from "@use/universal-hrtime";

import { Block } from "@apps/viewer/src/ts/components/block/Block";
import { colors } from "@apps/viewer/src/ts/config";
import { injectStore, Store } from "@apps/viewer/src/ts/store";

type EmbedBlockProps = {
    /**
     * A reference to the store that's backing the application lifecycle state.
     */
    store: Store;

    /**
     * A reference to the model representing the block that's currently being
     * rendered.
     */
    model: EmbedBlockModel;
};

type EmbedBlockState = {
    isLoaded: boolean;
};

/**
 * Specifies for how long the player at least should be waiting before appearing
 */
const MIN_LOADING_TIME = 500;

/**
 * Renders embeds (ie. iframe) codes entered by teachers.
 */
class EmbedBlockUnwrapped extends Block<EmbedBlockProps, EmbedBlockState> {
    /**
     * Setup initial state
     */
    public state: EmbedBlockState = {
        isLoaded: false,
    };

    /**
     * Contains a timestamp of when the block was rendered in order to determine
     * how long it took to ready the video
     */
    private readonly mountedAt: number = hrtime();

    private readonly throbberRef: RefObject<HTMLDivElement | null> = createRef();

    /**
     * Specifies whether or not the component is currently mounted
     */
    private componentIsMounted: boolean = true;

    /**
     * Indicate that component is umounted once the unmount lifecycle event
     * occurs
     */
    public componentWillUnmount(): void {
        this.componentIsMounted = false;
    }

    /**
     * Renderer that injects the content as entered by the user himself.
     */
    protected renderContent(): React.JSX.Element {
        return (
            <div>
                {this.renderEmbed()}
            </div>
        );
    }

    /**
     * Renderer that injects the content as entered by the user himself.
     */
    protected renderEmbed(): React.JSX.Element {
        const { model } = this.props;

        const aspectRatio = model.desiredHeight && model.desiredWidth
            ? model.desiredHeight / model.desiredWidth * 100
            : 0.75;

        const embedWidth = model.desiredWidth ? `${model.desiredWidth}px` : "100%";

        // tslint:disable react-iframe-missing-sandbox
        return (
            <div className="embed-aspect-ratio__wrapper">
                <div className="embed-aspect-ratio">
                    <div className={`embed-wrapper ${this.state.isLoaded ? "loaded" : ""}`}>
                        <iframe
                            src={model.src}
                            onLoad={this.onLoad}
                            frameBorder="0"
                            allowFullScreen
                        />
                        {this.renderThrobber()}
                    </div>
                </div>

                <style jsx>{`
                    .embed-aspect-ratio__wrapper {
                        /** Ensure desired width is obeyed */
                        width: ${ embedWidth};
                        max-width: 100%;

                        /** Center embed horizontally */
                        margin: 0 auto;
                    }

                    /**
                     * Ensure aspect ratio of desired width and height is obeyed
                     * if any is set
                     */
                    .embed-aspect-ratio {
                        /** Setup spacing between title and wrapper */
                        margin-top: 35px;

                        /** Create aspect-ratio */
                        position: relative;
                        width: 100%;
                        height: 0;
                        padding-bottom: ${ aspectRatio}%;
                    }

                    .embed-wrapper {
                        /** Let the actual wrapper fill the aspect ratio container */
                        position: absolute;
                        top: 0;
                        left: 0;
                        width: 100%;
                        height: 100%;

                        /** Background-color will be visible while video is loading */
                        background-color: #f7f7f7;

                        & iframe {
                            /** Let player expand to fill container */
                            width: 100%;
                            height: 100%;

                            /** Prepare enter transition */
                            opacity: 0;
                            transition: opacity 0.3s linear;
                        }

                        /** Once the embed is loaded, then it should be visible */
                        &.loaded {
                            & iframe {
                                opacity: 1;
                            }
                        }
                    }
                `}</style>
            </div>
        );
        // tslint:enable react-iframe-missing-sandbox
    }

    /**
     * Method that'll be called once the Youtube Player has properly loaded
     * in order to display the Youtube API
     */
    protected onLoad = async (): Promise<void> => {
        // Determine for how long we should wait before displaying the video
        // based on the specified min loading time.
        const elapsedTime = hrtime() - this.mountedAt;

        // We add 200ms no matter when to give any JS within the iFrame a chance
        // to settle before displaying content
        const timeout = Math.max(0, MIN_LOADING_TIME - elapsedTime) + 200;

        await asyncTimeout(timeout);

        if (this.componentIsMounted) {
            this.setState({ isLoaded: true });
        }
    }

    /**
     * Renderer that'll render a throbber that'll be displayed before the player
     * is ready
     */
    protected renderThrobber(): React.JSX.Element {
        return (
            <CSSTransition
                in={!this.state.isLoaded}
                unmountOnExit
                classNames="player-throbber"
                timeout={200}
                enter={false}
                nodeRef={this.throbberRef}
            >
                <React.Fragment>
                    <div className="player-throbber" ref={this.throbberRef} />

                    <style jsx>{`
                        .player-throbber {
                            /** Position throbber in the middle of the player wrapper */
                            position: absolute;
                            top: 50%;
                            left: 50%;
                            transform: translate(-50%, -50%);

                            /** Fixate dimensions of throbber */
                            height: 48px;
                            width: 48px;

                            /** Should be visible by default */
                            opacity: 1;

                            /** Prepare exit transition */
                            &.player-throbber-exit-active {
                                opacity: 0;
                                transition: opacity 0.2s linear;
                            }

                            /** Render the actual throbber using a pseudo element */
                            &::before {
                                /** Force rendering */
                                content: "";

                                /** Fill wrapper */
                                position: absolute;
                                top: 0;
                                left: 0;
                                width: 100%;
                                height: 100%;

                                /** Ensure borders doesn't overflow */
                                box-sizing: border-box;

                                /** Render the actual throbber */
                                border-radius: 50%;
                                border: 3px solid rgba(220, 67, 32, 0.25);
                                border-left-color: ${ colors.orange};

                                /** ... and make it spin forever */
                                animation: spin 0.9s linear infinite;
                            }
                        }

                        @keyframes spin {
                            from { transform: rotate(0deg); }
                            to { transform: rotate(360deg); }
                        }
                    `}</style>
                </React.Fragment>
            </CSSTransition>
        );
    }

    /**
     * Rendered that'll render the link of the block
     */
    protected renderLink(): React.JSX.Element {
        return (
            <button onClick={this.openEmbed}>
                <FormattedMessage
                    id="Block__embed__open"
                    defaultMessage="Åbn embed"
                />

                <style jsx>{`
                    button {
                        /** Override defaults */
                        margin: 0;
                        -webkit-appearance: none;
                        background-color: transparent;

                        /** Setup spacing between title and button */
                        margin-top: 35px;

                        /**
                         * setup dimensions of link
                         */
                        display: inline-block;
                        width: auto;
                        height: 41px;
                        line-height: 41px;
                        box-sizing: content-box;

                        /** Setup internal sizing */
                        padding: 0 45px;

                        /** Render link border */
                        border: 2px solid ${ colors.orange};

                        /** Indicate that button is clickable */
                        cursor: pointer;

                        /** Setup font */
                        font-size: 18px;
                        font-weight: 500;
                        text-transform: uppercase;
                        color: ${ colors.darkGrey};

                        /** Prepare hover transitions */
                        transition: color 0.1s linear, background-color 0.1s linear;

                        &:hover {
                            background-color: ${ colors.orange};
                            color: ${ colors.white};
                        }
                    }
                `}</style>
            </button>
        );
    }

    /**
     * Helper that, when called, will open the embed in a new tab
     */
    protected openEmbed = (): void => {
        const { model } = this.props;

        const openedWindow = window.open("", "_blank", `width=${model.desiredWidth},height=${model.desiredHeight}`);

        // If window was succesfully opened, write embedCode within
        if (openedWindow) {
            openedWindow.document.write(model.embedCode);
        }
    }
}

// tslint:disable-next-line variable-name
export const EmbedBlock = injectStore(EmbedBlockUnwrapped);
